import React from 'react';
import { RefreshCw, User, Mail, ShieldCheck, Building2, CheckCircle } from 'lucide-react';
import { DeleteRequest } from '../types';
import { Modal } from './Modal';

interface ReactivateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: DeleteRequest | null;
  onConfirm: () => void;
  loading?: boolean;
  success?: boolean;
  onSuccessClose?: () => void;
}

export const ReactivateAccountModal: React.FC<ReactivateAccountModalProps> = ({
  isOpen,
  onClose,
  request,
  onConfirm,
  loading = false,
  success = false,
  onSuccessClose,
}) => {
  if (!request) return null;

  const isProvider = request.accountType === 'provider';

  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={onSuccessClose || onClose} title="Reactivate Account">
        <div className="flex flex-col items-center justify-center py-8 space-y-5">
          <div className="flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full">
            <CheckCircle className="h-12 w-12 text-emerald-600" />
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold text-slate-900">Account Successfully Reactivated</h3>
            <p className="text-slate-600">
              <span className="font-medium">{request.name}</span>'s account has been successfully reactivated.
              They can now log in normally.
            </p>
          </div>
          <button
            onClick={onSuccessClose || onClose}
            className="mt-2 px-8 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
          >
            OK
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reactivate Account">
      <div className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold text-emerald-900 mb-1">
                Restore Account Access
              </h3>
              <p className="text-sm text-emerald-800">
                You are about to reactivate this account. The user will regain full access and be able to log in normally.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Account to be Reactivated:</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {isProvider ? (
                <Building2 className="h-4 w-4 text-slate-500" />
              ) : (
                <User className="h-4 w-4 text-slate-500" />
              )}
              <span className="text-sm text-slate-700">
                <span className="font-medium">Name:</span> {request.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Email:</span> {request.email}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isProvider ? (
                <Building2 className="h-4 w-4 text-slate-500" />
              ) : (
                <User className="h-4 w-4 text-slate-500" />
              )}
              <span className="text-sm text-slate-700">
                <span className="font-medium">Account Type:</span>{' '}
                {isProvider ? 'Provider' : 'Service User'}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-emerald-600" />
            What will be restored:
          </h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-emerald-600 font-bold mt-0.5">•</span>
              <span>Account login access and credentials</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-emerald-600 font-bold mt-0.5">•</span>
              <span>Account status set to active</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-emerald-600 font-bold mt-0.5">•</span>
              <span>Deletion flags cleared (isDeleted, deletedAt)</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-emerald-600 font-bold mt-0.5">•</span>
              <span>Associated delete request marked as restored</span>
            </li>
          </ul>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Reactivating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Reactivate Account
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};


